import React, { Component } from 'react';
import { Modal, Form, Input, Button, message, Alert, Upload, Row, Col } from 'antd';
import { UserOutlined, MailOutlined, UploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { accountId } from '../../../../config';
import './EditUserModal.scss';

const { TextArea } = Input;

class EditUserModal extends Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();

    this.state = {
      loading: false,
      error: '',
      uploadingStates: {
        image: false,
        imagePortrait: false,
        imageLandscape: false,
        imageBanner: false
      },
      imageUrls: {
        image: '',
        image360: '',
        imagePortrait: '',
        imageLandscape: '',
        imageBanner: ''
      },
      descriptionLength: 0
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && (!prevProps.visible || prevProps.userData !== this.props.userData)) {
      setTimeout(() => {
        this.initializeForm();
      }, 100);
    }
  }

  initializeForm = () => {
    const { userData } = this.props;

    if (userData && this.formRef.current) {
      const formValues = {
        name: userData.name || '',
        description: userData.description || '',
        vital: userData.vital || '',
        age: userData.age || '',
        height: userData.height || '',
        email: userData.email || ''
      };

      this.formRef.current.setFieldsValue(formValues);

      this.setState({
        imageUrls: {
          image: userData.image || '',
          image360: userData.image360 || '',
          imagePortrait: userData.imagePortrait || '',
          imageLandscape: userData.imageLandscape || '',
          imageBanner: userData.imageBanner || ''
        },
        descriptionLength: (userData.description || '').length
      });
    }
  };

  handleDescriptionChange = (e) => {
    this.setState({ descriptionLength: e.target.value.length });
  };

  handleImageUpload = async (file, imageType) => {
    const { onImageUpload } = this.props;

    this.setState(prevState => ({
      uploadingStates: {
        ...prevState.uploadingStates,
        [imageType]: true
      }
    }));

    try {
      const response = await onImageUpload({
        file: file,
        name: file.name,
        type: 'cast'
      });

      this.processUploadResponse(response, imageType);

    } catch (error) {
      console.error('Image upload error:', error);
      this.handleUploadError(error, imageType);
    }
  };

  processUploadResponse = (response, imageType) => {
    try {
      let imageData = response;

      if (response && response.data) {
        imageData = response.data;
      }

      const imageUrl = imageData.image || imageData.image360 || imageData.image480 || '';

      if (!imageUrl) {
        throw new Error('No image URL in response');
      }

      this.setState(prevState => {
        const newImageUrls = { ...prevState.imageUrls };

        if (imageType === 'image') {
          newImageUrls.image = imageUrl;
          newImageUrls.image360 = imageData.image360 || imageUrl;
        } else {
          newImageUrls[imageType] = imageUrl;
        }

        return {
          imageUrls: newImageUrls,
          uploadingStates: {
            ...prevState.uploadingStates,
            [imageType]: false
          }
        };
      });

      message.success('Image uploaded successfully');
    } catch (error) {
      this.handleUploadError(error, imageType);
    }
  };

  handleUploadError = (error, imageType) => {
    console.error(`${imageType} upload error:`, error);

    this.setState(prevState => ({
      uploadingStates: {
        ...prevState.uploadingStates,
        [imageType]: false
      }
    }));

    message.error(`Failed to upload ${imageType}`);
  };

  handleSubmit = async (values) => {
    const { onSubmit, userData } = this.props;
    const { imageUrls } = this.state;

    this.setState({ loading: true, error: '' });

    try {
      const updateData = {
        ...values,
        ...imageUrls
      };

      if (onSubmit) {
        await onSubmit(userData.id, updateData);
        message.success('User updated successfully');
        this.props.onClose();
      }
    } catch (error) {
      console.error('Update error:', error);
      this.setState({ error: error.message || 'Failed to update user' });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { visible, onClose } = this.props;
    const { loading, error, uploadingStates, imageUrls, descriptionLength } = this.state;
    const isHideImage = ['vivamax'].includes(accountId);

    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined />
            Edit Profile
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={900}
        destroyOnClose={true}
        className="edit-user-modal"
      >
        <div className="edit-user-modal-content">
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              className="error-alert"
            />
          )}

          <Form
            ref={this.formRef}
            layout="vertical"
            onFinish={this.handleSubmit}
            preserve={false}
            className="edit-user-form"
          >
            <Row gutter={32}>
              {/* Left Column - Form Fields */}
              <Col span={isHideImage ? 24 : 12}>
                <Form.Item
                  name="name"
                  label={<span>Name</span>}
                  rules={[{ required: true, message: 'Please input user name!' }]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Enter name"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="description"
                  label={<span>Description</span>}
                >
                  <TextArea
                    rows={3}
                    placeholder="Enter description"
                    maxLength={100}
                    onChange={this.handleDescriptionChange}
                    style={{ resize: 'none' }}
                  />
                </Form.Item>
                <div style={{ textAlign: 'right', color: '#999', fontSize: '12px', marginTop: "-15px" }}>
                  {descriptionLength} / 100
                </div>

                <Form.Item name="vital" label="Bio">
                  <TextArea
                    rows={4}
                    placeholder="Bio"
                    style={{ resize: 'none' }}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="age" label="Age">
                      <Input placeholder="Age" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="height" label="Height">
                      <Input placeholder="Height" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please input email!' },
                    { type: 'email', message: 'Please enter a valid email!' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="Enter email address"
                    size="large"
                  />
                </Form.Item>
              </Col>

              {/* Right Column - Image Uploads */}
              {
                !isHideImage &&
                <Col span={12}>
                  <div className="image-upload-section">
                    {/* User Icon */}
                    <div className="upload-item" style={{ marginBottom: 24 }}>
                      <div className="upload-label">User Icon</div>
                      <div className="image-upload-container">
                        {imageUrls.image && (
                          <div className="image-preview-wrapper">
                            <img
                              src={imageUrls.image}
                              alt="User Icon"
                              className="image-preview"
                              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                        <Upload
                          beforeUpload={(file) => {
                            this.handleImageUpload(file, 'image');
                            return false;
                          }}
                          showUploadList={false}
                        >
                          <Button
                            type="link"
                            icon={uploadingStates.image ? <LoadingOutlined /> : <UploadOutlined />}
                            loading={uploadingStates.image}
                            style={{ color: '#1890ff', padding: '8px' }}
                          >
                            Change Icon
                          </Button>
                        </Upload>
                      </div>
                    </div>

                    {/* Portrait and Landscape */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                      <Col span={12}>
                        <div className="upload-item">
                          <div className="upload-label">Portrait</div>
                          <div className="upload-box">
                            {imageUrls.imagePortrait ? (
                              <img
                                src={imageUrls.imagePortrait}
                                alt="Portrait"
                                style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }}
                              />
                            ) : (
                              <div className="upload-placeholder">
                                <Upload
                                  beforeUpload={(file) => {
                                    this.handleImageUpload(file, 'imagePortrait');
                                    return false;
                                  }}
                                  showUploadList={false}
                                >
                                  <Button
                                    type="link"
                                    icon={uploadingStates.imagePortrait ? <LoadingOutlined /> : <UploadOutlined />}
                                    loading={uploadingStates.imagePortrait}
                                    style={{ color: '#1890ff' }}
                                  >
                                    Upload
                                  </Button>
                                </Upload>
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div className="upload-item">
                          <div className="upload-label">Landscape</div>
                          <div className="upload-box">
                            {imageUrls.imageLandscape ? (
                              <img
                                src={imageUrls.imageLandscape}
                                alt="Landscape"
                                style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }}
                              />
                            ) : (
                              <div className="upload-placeholder">
                                <Upload
                                  beforeUpload={(file) => {
                                    this.handleImageUpload(file, 'imageLandscape');
                                    return false;
                                  }}
                                  showUploadList={false}
                                >
                                  <Button
                                    type="link"
                                    icon={uploadingStates.imageLandscape ? <LoadingOutlined /> : <UploadOutlined />}
                                    loading={uploadingStates.imageLandscape}
                                    style={{ color: '#1890ff' }}
                                  >
                                    Upload
                                  </Button>
                                </Upload>
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Hero Image */}
                    <div className="upload-item">
                      <div className="upload-label">Hero</div>
                      <div className="upload-box" style={{ height: 160 }}>
                        {imageUrls.imageBanner ? (
                          <img
                            src={imageUrls.imageBanner}
                            alt="Hero"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                          />
                        ) : (
                          <div className="upload-placeholder">
                            <Upload
                              beforeUpload={(file) => {
                                this.handleImageUpload(file, 'imageBanner');
                                return false;
                              }}
                              showUploadList={false}
                            >
                              <Button
                                type="link"
                                icon={uploadingStates.imageBanner ? <LoadingOutlined /> : <UploadOutlined />}
                                loading={uploadingStates.imageBanner}
                                style={{ color: '#1890ff' }}
                              >
                                Upload
                              </Button>
                            </Upload>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              }

            </Row>

            <div style={{ textAlign: 'right', marginTop: 32, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <Button onClick={onClose} style={{ marginRight: 12 }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<UserOutlined />}
                size="large"
              >
                Save
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    );
  }
}

export default EditUserModal;